import React, { useCallback, useMemo, useState } from 'react';
import { ArrowUpRight, Eye, FolderOpen, MessageSquareText, UserCheck } from 'lucide-react';
import { adminApi } from '../../services/api.jsx';
import { annoncerSucces } from '../../etats';
import ApercuDocument from './ApercuDocument';
import { TuileFichier, typeDeFichier } from './typeDeFichier';

/* ═══════════════════════════════════════════════════════════════════════
   LES DOCUMENTS DÉPOSÉS — une liste qu'on parcourt, pas un mur de cartes

   Chaque document tenait une carte entière, avec sa zone de commentaire
   et deux boutons pleine largeur, même quand on voulait seulement le lire.
   Ici : une ligne par document, les filtres par statut avec leur compte,
   et un clic qui ouvre la visionneuse — où l'on lit puis tranche.
   ═══════════════════════════════════════════════════════════════════════ */

const STATUTS = {
  soumis: { libelle: 'À examiner', classe: 'etiq-attente' },
  valide: { libelle: 'Validé', classe: 'etiq-ok' },
  rejete: { libelle: 'Rejeté', classe: 'etiq-refus' },
};

const FILTRES = [['tous', 'Tous'], ['soumis', 'À examiner'], ['valide', 'Validés'], ['rejete', 'Rejetés']];

function quand(iso) {
  const d = new Date(iso);
  const jours = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (jours <= 0) return 'aujourd’hui';
  if (jours === 1) return 'hier';
  if (jours < 7) return `il y a ${jours} jours`;
  return `le ${d.toLocaleDateString('fr-FR')}`;
}

export default function ListeDocuments({ documents, peutExaminer, onChange, onOuvrirProjet, vide }) {
  const [filtre, setFiltre] = useState('tous');
  const [ouvert, setOuvert] = useState(null);

  const comptes = useMemo(() => {
    const c = { tous: documents.length, soumis: 0, valide: 0, rejete: 0 };
    documents.forEach((d) => { c[d.statut] = (c[d.statut] || 0) + 1; });
    return c;
  }, [documents]);

  const visibles = useMemo(() => {
    const liste = filtre === 'tous' ? documents : documents.filter((d) => d.statut === filtre);
    // Ce qui attend une décision passe devant, puis le plus récent.
    return [...liste].sort((a, b) =>
      (a.statut === 'soumis' ? 0 : 1) - (b.statut === 'soumis' ? 0 : 1)
      || new Date(b.upload_le) - new Date(a.upload_le));
  }, [documents, filtre]);

  const decider = useCallback(async (doc, statut, commentaire) => {
    await adminApi.validerDocument(doc.id, statut, commentaire);
    annoncerSucces(statut === 'valide'
      ? `« ${doc.nom_fichier} » validé. L’organisation est prévenue.`
      : `« ${doc.nom_fichier} » rejeté. L’organisation est invitée à le refaire.`);
    await onChange?.();
    // On enchaîne sur la pièce suivante qui attend, s'il y en a une.
    const suivante = visibles.find((d) => d.statut === 'soumis' && d.id !== doc.id);
    setOuvert(suivante ? suivante.id : doc.id);
  }, [onChange, visibles]);

  const fermer = useCallback(() => setOuvert(null), []);

  if (documents.length === 0) {
    return vide || (
      <div className="text-center py-10 text-[var(--text-muted)]">
        <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p className="font-semibold text-sm">Aucun document déposé</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-1.5 flex-wrap mb-3" role="tablist" aria-label="Filtrer par statut">
        {FILTRES.map(([cle, libelle]) => (
          <button key={cle} type="button" role="tab" aria-selected={filtre === cle} onClick={() => setFiltre(cle)}
                  className={`act act-sm ${filtre === cle ? 'act-fort' : 'act-doux'}`}>
            {libelle}
            <span className={`tabular-nums text-[11px] px-1.5 rounded-controle ${filtre === cle ? 'bg-white/20' : 'bg-[var(--bg-card)]'}`}>
              {comptes[cle] || 0}
            </span>
          </button>
        ))}
      </div>

      {visibles.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)] py-8 text-center">Aucun document dans cette catégorie.</p>
      ) : (
        <ul className="bg-[var(--bg-card)] rounded-carte shadow-carte divide-y divide-[var(--border)] overflow-hidden">
          {visibles.map((doc) => {
            const st = STATUTS[doc.statut] || STATUTS.soumis;
            const type = typeDeFichier(doc);
            return (
              <li key={doc.id} className="group flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 hover:bg-[var(--bg-main)] transition-colors">
                <button type="button" onClick={() => setOuvert(doc.id)} className="shrink-0" aria-label={`Voir ${doc.nom_fichier}`}>
                  <TuileFichier doc={doc} />
                </button>
                <div className="min-w-0 flex-1">
                  <button type="button" onClick={() => setOuvert(doc.id)}
                          className="block max-w-full text-left text-[14px] font-semibold text-[var(--text-bold)] truncate hover:underline underline-offset-2">
                    {doc.nom_fichier}
                  </button>
                  <p className="text-[12px] text-[var(--text-muted)] flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                    <span>{type.libelle}</span>
                    <span aria-hidden="true">·</span>
                    <span className="capitalize">{doc.categorie}</span>
                    <span aria-hidden="true">·</span>
                    <span>Déposé {quand(doc.upload_le)}</span>
                    {doc.projet_nature && (<><span aria-hidden="true">·</span><span>{doc.projet_nature}</span></>)}
                  </p>
                  {(doc.demande_libelle || doc.depose_par_equipe || doc.commentaire_coach) && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {doc.demande_libelle && (
                        <span className="etiquette etiq-neutre text-[11px]">Répond à : {doc.demande_libelle}</span>
                      )}
                      {doc.depose_par_equipe && (
                        <span className="etiquette etiq-cours text-[11px]"><UserCheck className="w-3 h-3" />Déposé par {doc.depose_par_display || 'l’équipe'}</span>
                      )}
                      {doc.commentaire_coach && (
                        <span className="etiquette etiq-neutre text-[11px] max-w-full" title={doc.commentaire_coach}>
                          <MessageSquareText className="w-3 h-3 shrink-0" /><span className="truncate">{doc.commentaire_coach}</span>
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <span className={`etiquette ${st.classe} shrink-0 hidden sm:inline-flex`}>{st.libelle}</span>
                <div className="flex items-center gap-1 shrink-0">
                  {peutExaminer && doc.statut === 'soumis' ? (
                    <button type="button" onClick={() => setOuvert(doc.id)} className="act act-sm">
                      <Eye className="w-4 h-4" /> Examiner
                    </button>
                  ) : (
                    <button type="button" onClick={() => setOuvert(doc.id)} className="act act-sm act-contour">
                      <Eye className="w-4 h-4" /> <span className="hidden sm:inline">Voir</span>
                    </button>
                  )}
                  {onOuvrirProjet && (
                    <button type="button" onClick={() => onOuvrirProjet(doc)} className="act act-icone act-tertiaire act-sm"
                            aria-label="Ouvrir le projet" title="Ouvrir le projet">
                      <ArrowUpRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {ouvert && (
        <ApercuDocument documents={visibles.some((d) => d.id === ouvert) ? visibles : documents}
                        docId={ouvert} onNaviguer={setOuvert} onFermer={fermer}
                        onDecider={decider} peutExaminer={peutExaminer} />
      )}
    </div>
  );
}
